import React, { Dispatch, SetStateAction } from "react"

import { Field, Form, Formik } from "formik"
import { NotificationManager } from "react-notifications"
import { useHistory } from "react-router-dom"
import { gql, useMutation } from "urql"
import * as Yup from "yup"

import {
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useTheme,
} from "@chakra-ui/core"

import {
  AutocompleteField,
  FormFailures,
  handleFailure,
} from "components/Forms/Formik/hookComponents"

export type NewThreadModalProps = {
  modalOpen: boolean
  setModalOpen: Dispatch<SetStateAction<boolean>>
}

const ValidationSchema = Yup.object().shape({
  userIds: Yup.array().required("You must select at least one recipient"),
  title: Yup.string().required("Please specify a title for this thread"),
})

const NewThreadModal: React.FC<NewThreadModalProps> = ({
  modalOpen,
  setModalOpen,
}) => {
  const history = useHistory()
  const theme: any = useTheme()

  const formState = {
    userIds: [],
    title: "",
  }

  const [, runMutation] = useMutation(createThreadMutation)

  const handleSubmit = (values, actions) => {
    runMutation(values)
      .then(
        result => {
          const { failures, messageThread } = result.data.createMessageThread
          if (failures.length > 0) {
            handleFailure(actions, failures)
          } else {
            NotificationManager.info("Thread created")
            history.push(`/messages/${messageThread.id}`)
            setModalOpen(false)
          }
        },
        () => handleFailure(actions)
      )
      .catch(() => handleFailure(actions))
  }

  return (
    <>
      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)}>
        <ModalOverlay />
        <ModalContent>
          <Formik
            initialValues={formState}
            validationSchema={ValidationSchema}
            onSubmit={handleSubmit}
          >
            {({ isSubmitting }) => (
              <Form>
                <ModalHeader>New Message Thread</ModalHeader>
                <ModalBody>
                  <Field name="userIds">
                    {({ field, form }) => (
                      <FormControl isInvalid={!!form.errors[field.name]} mt="4">
                        <AutocompleteField
                          api="/api/teachers/message_threads/autocomplete_messageable_users"
                          name="userIds"
                          isMulti
                          label="Recipient Name(s)"
                          placeholder="Type Tutor Name..."
                          hideError
                        />
                        <FormErrorMessage>
                          {form.errors[field.name]}
                        </FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>

                  <Field name="title">
                    {({ field, form }) => (
                      <FormControl isInvalid={!!form.errors[field.name]} mt="4">
                        <FormLabel htmlFor={field.name}>Thread Title</FormLabel>
                        <Flex
                          align={{ md: "center" }}
                          direction={["column", "column", "row"]}
                        >
                          <Input
                            id={field.name}
                            {...field}
                            flexBasis="63%"
                            placeholder="ex, 6th Grade Math"
                          />
                        </Flex>
                        <FormErrorMessage>
                          {form.errors[field.name]}
                        </FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                </ModalBody>

                <ModalFooter>
                  <div className="flex w-full flex-col space-y-4">
                    <FormFailures />
                    <div className="flex w-full justify-between">
                      <Button
                        {...theme.buttonVariants.view}
                        onClick={() => setModalOpen(false)}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        {...theme.buttonVariants.cloudroom}
                        disabled={isSubmitting}
                      >
                        Submit
                      </Button>
                    </div>
                  </div>
                </ModalFooter>
              </Form>
            )}
          </Formik>
        </ModalContent>
      </Modal>
    </>
  )
}

const createThreadMutation = gql`
  mutation CreateMessageThread($userIds: [ID!]!, $title: String!) {
    createMessageThread(userIds: $userIds, title: $title) {
      failures {
        message
      }
      messageThread {
        id
      }
    }
  }
`

export default NewThreadModal
